/**
 * ScubaDeepScanTab — editorial redesign per the rebrand pass.
 *
 * Wraps the on-demand CISA SCuBA assessment in the same editorial chrome as
 * the sister Security Center tabs (BPA, Secure Score, App Posture):
 *
 * - Light navy-on-paper surfaces, no dark-* utility classes.
 * - SectionEyebrow + SectionHeadline at every section break.
 * - Severity-tinted dropcaps and result chips drawn from the rebrand palette.
 * - Navy editorial CTA pill replaces the cyan-glow gradient button.
 *
 * Functional preserves: scan-job polling, PowerShell-backed scan kickoff,
 * product/group expansion state, the dompurify sanitization on `details`,
 * the imperative `refresh()` / `startScan()` ref API, and the cross-link to
 * the Best Practice Analyzer.
 */

import { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import type {
  ScubaProductResult,
  ScubaProductSummary,
  ScubaControlGroup,
  ScubaControl,
  ScubaControlResult,
  ScubaSummary,
} from '@/types'
import { getScubaProductInfo as getProductInfo } from '@/types/scuba'
import { SectionEyebrow, SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import PillarBrief from '@/components/PostureBrief/PillarBrief'
import type { PostureBrief } from '@/lib/posture-brief'
import {
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  EnvelopeIcon,
  FolderIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline'

/** Ref interface for programmatic control of the SCuBA tab. */
export interface ScubaDeepScanTabRef {
  refresh: () => void
  startScan: () => void
}

export interface ScubaDeepScanTabProps {
  tenantId: string | undefined
  onStatusChange?: (status: 'idle' | 'scanning' | 'completed' | 'error') => void
}

/** Allow-listed HTML in `control.details` so vendor-supplied remediation links render as anchors. */
const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['a', 'br', 'strong', 'em', 'p', 'ul', 'ol', 'li', 'span', 'b', 'i'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    ADD_ATTR: ['target', 'rel'],
  })
}

const getProductIcon = (productName: string) => {
  switch (productName.toLowerCase()) {
    case 'aad':         return <ShieldCheckIcon className="h-5 w-5" />
    case 'sharepoint':  return <FolderIcon className="h-5 w-5" />
    case 'exo':         return <EnvelopeIcon className="h-5 w-5" />
    case 'teams':       return <ChatBubbleLeftRightIcon className="h-5 w-5" />
    case 'defender':    return <ShieldExclamationIcon className="h-5 w-5" />
    default:            return <ShieldCheckIcon className="h-5 w-5" />
  }
}

/* -------------------------------------------------------------------------- */
/* Editorial palette — local helpers                                          */
/* -------------------------------------------------------------------------- */

/** Severity-tinted color for a compliance score. Same ramp as the heatmap on
 *  Security Center Overview so a 73% score reads "watch" everywhere. */
function scoreTone(score: number): string {
  if (score >= 90) return '#4A9B6E'
  if (score >= 70) return '#4F86A8'
  if (score >= 50) return '#C9AD80'
  return '#C84B4B'
}

/** Editorial chip styling for each SCuBA control result. Replaces the legacy
 *  Tailwind `bg-{green,red,yellow,…}-100 text-{…}-800` pairs from types/scuba. */
const RESULT_CHIP: Record<ScubaControlResult, { color: string; background: string; border: string; label: string }> = {
  Pass:          { color: '#3F8259', background: 'rgba(118,214,157,0.20)', border: 'rgba(74,155,110,0.45)',  label: 'Pass'   },
  Fail:          { color: '#A03434', background: 'rgba(229,115,115,0.16)', border: 'rgba(200,75,75,0.45)',   label: 'Fail'   },
  Warning:       { color: '#8C6B22', background: 'rgba(235,212,180,0.45)', border: 'rgba(201,173,128,0.50)', label: 'Warn'   },
  Error:         { color: '#A03434', background: 'rgba(229,115,115,0.10)', border: 'rgba(200,75,75,0.35)',   label: 'Error'  },
  NotApplicable: { color: '#6B7280', background: 'rgba(156,163,175,0.16)', border: 'rgba(156,163,175,0.40)', label: 'N/A'    },
}

/** Result-tinted left-border accent on each control card. */
function controlBorderAccent(result: ScubaControlResult): string {
  return RESULT_CHIP[result]?.border ?? 'rgba(156,163,175,0.30)'
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const ScubaDeepScanTab = forwardRef<ScubaDeepScanTabRef, ScubaDeepScanTabProps>(
  function ScubaDeepScanTab({ tenantId, onStatusChange }, ref) {
    const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set())
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

    const [scanJobId, setScanJobId] = useState<string | null>(null)
    const [pollInterval, setPollInterval] = useState<number | null>(null)

    const {
      data: summary,
      isLoading: summaryLoading,
      refetch: refetchSummary,
    } = useQuery({
      queryKey: ['scuba-summary', tenantId],
      queryFn: () => api.getScubaSummary(tenantId!),
      enabled: !!tenantId,
    })

    const {
      data: report,
      isLoading: reportLoading,
      refetch: refetchReport,
    } = useQuery({
      queryKey: ['scuba-report', tenantId],
      queryFn: () => api.getScubaReport(tenantId!),
      enabled: !!tenantId && summary?.hasReport === true,
    })

    const { data: jobStatus } = useQuery({
      queryKey: ['scan-job', scanJobId],
      queryFn: () => api.getScanJobStatus(scanJobId!),
      enabled: !!scanJobId,
      refetchInterval: pollInterval || false,
    })

    useEffect(() => {
      if (!jobStatus) return
      if (jobStatus.status === 'Completed') {
        setPollInterval(null)
        setScanJobId(null)
        toast.success('SCuBA scan completed successfully!')
        onStatusChange?.('completed')
        refetchSummary()
        refetchReport()
      } else if (jobStatus.status === 'Failed') {
        setPollInterval(null)
        setScanJobId(null)
        toast.error(`SCuBA scan failed: ${jobStatus.errorMessage || 'Unknown error'}`)
        onStatusChange?.('error')
      } else if (jobStatus.status === 'Running' || jobStatus.status === 'Queued') {
        setPollInterval(3000)
        onStatusChange?.('scanning')
      }
    }, [jobStatus, refetchSummary, refetchReport, onStatusChange])

    const startScanMutation = useMutation({
      mutationFn: () => api.startScubaScan(tenantId!),
      onSuccess: (response) => {
        setScanJobId(response.jobId)
        setPollInterval(3000)
        onStatusChange?.('scanning')
        toast.success('SCuBA scan started! This may take several minutes…')
      },
      onError: (error: Error) => {
        onStatusChange?.('error')
        toast.error(`Failed to start SCuBA scan: ${error.message || 'Unknown error'}`)
      },
    })

    const handleStartScan = useCallback(() => {
      if (!tenantId) {
        toast.error('Please select a tenant first')
        return
      }
      startScanMutation.mutate()
    }, [tenantId, startScanMutation])

    const handleRefresh = useCallback(() => {
      refetchSummary()
      refetchReport()
    }, [refetchSummary, refetchReport])

    useImperativeHandle(
      ref,
      () => ({ refresh: handleRefresh, startScan: handleStartScan }),
      [handleRefresh, handleStartScan],
    )

    const toggleProduct = (productName: string) => {
      setExpandedProducts((prev) => {
        const next = new Set(prev)
        if (next.has(productName)) next.delete(productName)
        else next.add(productName)
        return next
      })
    }

    const toggleGroup = (groupKey: string) => {
      setExpandedGroups((prev) => {
        const next = new Set(prev)
        if (next.has(groupKey)) next.delete(groupKey)
        else next.add(groupKey)
        return next
      })
    }

    const isScanning =
      scanJobId !== null &&
      (jobStatus?.status === 'Running' || jobStatus?.status === 'Queued')

    /* ----------- No tenant ------------------------------------------------- */
    if (!tenantId) {
      return (
        <div data-tone="bpa">
          <NoTenantState />
        </div>
      )
    }

    /* ----------- Loading --------------------------------------------------- */
    if (summaryLoading) {
      return (
        <div data-tone="bpa">
          <LoadingState />
        </div>
      )
    }

    /* ----------- No report yet — editorial intro --------------------------- */
    if (!summary?.hasReport) {
      return (
        <div data-tone="bpa">
          <IntroHero
            onStart={handleStartScan}
            isScanning={isScanning || startScanMutation.isPending}
            currentStep={jobStatus?.currentStep}
          />
        </div>
      )
    }

    /* ----------- Main report view ----------------------------------------- */
    const score = Math.round(summary?.complianceScore ?? 0)
    const tone = scoreTone(score)

    // SCuBA-specific brief. The parent SecurityCenter no longer renders its
    // Guardian-composite header on this tab (see SecurityCenter.tsx), so this
    // brief owns the standfirst. The right-rail uses a custom `aside` instead
    // of the default ScoreBox because SCuBA findings don't roll into Guardian
    // Score — surfacing a 0-100 score box would mis-frame the page as a
    // continuous metric. The aside also pulls the run button up so it's
    // immediately reachable, replacing the legacy top action bar.
    const scubaBrief = composeScubaBrief(summary)
    const scanInProgress = isScanning || startScanMutation.isPending

    return (
      <div data-tone="bpa">
        <PillarBrief
          brief={scubaBrief}
          score={null}
          components={[]}
          tone="bpa"
          aside={
            <ScubaScanAside
              score={score}
              tone={tone}
              summary={summary}
              isScanning={scanInProgress}
              currentStep={jobStatus?.currentStep}
              onScan={handleStartScan}
            />
          }
          footerNote="SCuBA Deep is a point-in-time audit. Findings here don't roll into **Guardian Score** — track ongoing alignment in **Best Practice**."
        />

        {/* Section · 01 — Posture snapshot */}
        <section className="mt-10">
          <SectionEyebrow text="Section · 01 · Posture snapshot" />
          <SectionHeadline level="page">
            Where the deep scan <em>lands today.</em>
          </SectionHeadline>
          <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
            One overall score across {summary?.totalControls ?? 0} CISA SCuBA controls — drilled down by result.
          </p>

          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <ScoreTile score={score} tone={tone} />
            <ResultTile label="Passed"   value={summary?.totalPasses   ?? 0} icon={<CheckCircleIcon className="h-4 w-4" />} accent={RESULT_CHIP.Pass.color} />
            <ResultTile label="Failed"   value={summary?.totalFailures ?? 0} icon={<XCircleIcon className="h-4 w-4" />}      accent={RESULT_CHIP.Fail.color} />
            <ResultTile label="Warnings" value={summary?.totalWarnings ?? 0} icon={<ExclamationTriangleIcon className="h-4 w-4" />} accent={RESULT_CHIP.Warning.color} />
            <ResultTile label="Errors"   value={summary?.totalErrors   ?? 0} icon={<XCircleIcon className="h-4 w-4" />}      accent={RESULT_CHIP.Error.color} />
            <ResultTile label="Manual"   value={summary?.totalManual   ?? 0} icon={<InformationCircleIcon className="h-4 w-4" />}    accent={RESULT_CHIP.NotApplicable.color} />
          </div>
        </section>

        {/* Section · 02 — By workload */}
        <section className="mt-10">
          <SectionEyebrow text="Section · 02 · By workload" />
          <SectionHeadline level="page">
            Compliance <em>by workload.</em>
          </SectionHeadline>
          <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
            Click a workload to expand its control groups and individual findings.
          </p>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {(report?.products || summary?.products || []).map((product: ScubaProductResult | ScubaProductSummary) => {
              const info = getProductInfo(product.productName)
              const expanded = expandedProducts.has(product.productName)
              const productScore = Math.round(product.complianceScore)
              const pTone = scoreTone(productScore)

              return (
                <button
                  key={product.productName}
                  onClick={() => toggleProduct(product.productName)}
                  aria-expanded={expanded}
                  className={[
                    'group flex flex-col items-start gap-3 rounded-[4px] border bg-[color:var(--color-paper,#fff)] p-4 text-left transition-shadow',
                    expanded
                      ? 'border-[color:var(--color-navy,#212938)] shadow-[0_4px_14px_rgba(33,41,56,0.10)]'
                      : 'border-[color:var(--color-line,#E6E8EC)] hover:shadow-md',
                  ].join(' ')}
                >
                  <div className="flex w-full items-center justify-between">
                    <span
                      className="inline-flex h-8 w-8 items-center justify-center rounded-[3px]"
                      style={{ background: 'rgba(33,41,56,0.06)', color: 'var(--color-navy,#212938)' }}
                    >
                      {getProductIcon(product.productName)}
                    </span>
                    <span
                      className="font-display text-[22px] font-extrabold leading-none tabular-nums"
                      style={{ color: pTone, letterSpacing: '-0.02em' }}
                    >
                      {productScore}%
                    </span>
                  </div>

                  <div className="text-[13px] font-bold text-[color:var(--color-navy,#212938)]">
                    {info.displayName}
                  </div>

                  <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--color-mute,#6B7280)]">
                    {product.passes} passed · {product.failures} failed
                    {'warnings' in product && product.warnings > 0 ? ` · ${product.warnings} warn` : ''}
                  </div>
                </button>
              )
            })}
          </div>
        </section>

        {/* Section · 03 — Control detail (only when at least one product is expanded) */}
        {expandedProducts.size > 0 && (
          <section className="mt-10">
            <SectionEyebrow text="Section · 03 · Control detail" />
            <SectionHeadline level="page">
              The findings <em>up close.</em>
            </SectionHeadline>

            <div className="mt-6 space-y-6">
              {(report?.products || summary?.products || [])
                .filter((p: ScubaProductResult | ScubaProductSummary) => expandedProducts.has(p.productName))
                .map((product: ScubaProductResult | ScubaProductSummary) => {
                  const info = getProductInfo(product.productName)
                  const fullProduct = (report?.products ?? []).find(
                    (p) => p.productName === product.productName,
                  ) as ScubaProductResult | undefined
                  const controlGroups = fullProduct?.controlGroups
                  const productScore = Math.round(product.complianceScore)
                  const pTone = scoreTone(productScore)

                  return (
                    <article
                      key={product.productName}
                      className="overflow-hidden rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)]"
                    >
                      <header className="flex items-center justify-between gap-4 border-b border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-cream,#FBF8F2)] px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span
                            className="inline-flex h-9 w-9 items-center justify-center rounded-[3px]"
                            style={{ background: 'rgba(33,41,56,0.06)', color: 'var(--color-navy,#212938)' }}
                          >
                            {getProductIcon(product.productName)}
                          </span>
                          <div>
                            <div className="text-[14px] font-bold text-[color:var(--color-navy,#212938)]">
                              {info.displayName}
                            </div>
                            <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--color-mute,#6B7280)]">
                              {product.passes} passed · {product.failures} failed
                              {'warnings' in product && product.warnings > 0 ? ` · ${product.warnings} warn` : ''}
                            </div>
                          </div>
                        </div>
                        <span
                          className="font-display text-[26px] font-extrabold leading-none tabular-nums"
                          style={{ color: pTone, letterSpacing: '-0.02em' }}
                        >
                          {productScore}%
                        </span>
                      </header>

                      {reportLoading && !controlGroups && (
                        <div className="px-6 py-8 text-center">
                          <ArrowPathIcon className="mx-auto h-5 w-5 animate-spin text-[color:var(--color-mute,#6B7280)]" />
                          <p className="mt-2 text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
                            Loading control details…
                          </p>
                        </div>
                      )}

                      {!reportLoading && controlGroups && controlGroups.length === 0 && (
                        <div className="px-6 py-8 text-center text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
                          No detailed control data available for this workload.
                        </div>
                      )}

                      {controlGroups && controlGroups.length > 0 && (
                        <ul className="divide-y divide-[color:var(--color-line,#E6E8EC)]">
                          {controlGroups.map((group: ScubaControlGroup) => {
                            const groupKey = `${product.productName}-${group.groupNumber}`
                            const isOpen = expandedGroups.has(groupKey)
                            const failures = group.failures || 0
                            const warnings = group.warnings || 0

                            return (
                              <li key={groupKey}>
                                <button
                                  onClick={() => toggleGroup(groupKey)}
                                  aria-expanded={isOpen}
                                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-[color:var(--color-cream,#FBF8F2)]"
                                >
                                  <div className="flex min-w-0 items-center gap-3">
                                    {isOpen ? (
                                      <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-[color:var(--color-mute,#6B7280)]" />
                                    ) : (
                                      <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-[color:var(--color-mute,#6B7280)]" />
                                    )}
                                    <div className="min-w-0">
                                      <div className="truncate text-[13px] font-bold text-[color:var(--color-navy,#212938)]">
                                        {group.groupNumber}. {group.groupName}
                                      </div>
                                      <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--color-mute,#6B7280)]">
                                        {group.passes} passed · {failures} failed
                                        {warnings > 0 && ` · ${warnings} warn`}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex flex-shrink-0 items-center gap-2">
                                    {failures > 0 && (
                                      <ResultPill result="Fail" count={failures} suffix="failed" />
                                    )}
                                    {warnings > 0 && (
                                      <ResultPill result="Warning" count={warnings} suffix="warn" />
                                    )}
                                    {failures === 0 && warnings === 0 && (
                                      <ResultPill result="Pass" suffix="all passed" />
                                    )}
                                  </div>
                                </button>

                                {isOpen && (
                                  <div className="bg-[color:var(--color-cream,#FBF8F2)] px-5 pb-5 pt-2">
                                    <div className="space-y-3">
                                      {group.controls?.map((control: ScubaControl, idx: number) => (
                                        <ControlCard key={`${groupKey}-${idx}`} control={control} />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </article>
                  )
                })}
            </div>
          </section>
        )}
      </div>
    )
  },
)

export default ScubaDeepScanTab

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

function NoTenantState() {
  return (
    <div className="rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-12 py-12 text-center">
      <ShieldCheckIcon className="mx-auto h-12 w-12 text-[color:var(--color-faint,#9CA3AF)]" />
      <h3 className="mt-4 text-lg font-semibold text-[color:var(--color-navy,#212938)]">Select a tenant</h3>
      <p className="mt-1 text-sm italic text-[color:var(--color-mute,#6B7280)]">
        Choose a tenant from the selector above to run a SCuBA deep scan.
      </p>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] px-12 py-12 text-center">
      <ArrowPathIcon className="mx-auto h-10 w-10 animate-spin text-[color:var(--color-mute,#6B7280)]" />
      <p className="mt-3 text-sm italic text-[color:var(--color-mute,#6B7280)]">
        Loading SCuBA compliance data…
      </p>
    </div>
  )
}

function IntroHero({
  onStart,
  isScanning,
  currentStep,
}: {
  onStart: () => void
  isScanning: boolean
  currentStep?: string | null
}) {
  return (
    <>
      <section className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)] px-8 py-10">
        <SectionEyebrow text="Section · 01 · CISA SCuBA" />
        <SectionHeadline level="page">
          Run a <em>deep scan.</em>
        </SectionHeadline>
        <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-[color:var(--color-navy,#212938)]">
          SCuBA evaluates this Microsoft 365 tenant against CISA's Secure Cloud Business Applications baselines —
          across Entra ID, SharePoint, Exchange Online, Teams, and Defender.
          <span className="italic text-[color:var(--color-mute,#6B7280)]">
            {' '}A scan takes several minutes and does not modify any tenant configuration.
          </span>
        </p>

        <div className="mt-6">
          <button
            onClick={onStart}
            disabled={isScanning}
            className="inline-flex items-center gap-2 rounded-full bg-[#212938] px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#0F1722] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isScanning ? (
              <>
                <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                {currentStep || 'Starting scan…'}
              </>
            ) : (
              <>
                <PlayIcon className="h-3.5 w-3.5" />
                Run your first scan
              </>
            )}
          </button>
        </div>
      </section>

      <section className="mt-10">
        <SectionEyebrow text="Section · 02 · Workloads assessed" />
        <SectionHeadline level="page">
          Five workloads <em>under the lens.</em>
        </SectionHeadline>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {(['aad', 'sharepoint', 'exo', 'teams', 'defender'] as const).map((product) => {
            const info = getProductInfo(product)
            return (
              <div
                key={product}
                className="flex flex-col items-start gap-3 rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] p-4"
              >
                <span
                  className="inline-flex h-8 w-8 items-center justify-center rounded-[3px]"
                  style={{ background: 'rgba(33,41,56,0.06)', color: 'var(--color-navy,#212938)' }}
                >
                  {getProductIcon(product)}
                </span>
                <div className="text-[13px] font-bold text-[color:var(--color-navy,#212938)]">
                  {info.displayName}
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <p className="mt-8 text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
        Already monitoring continuously? Track ongoing SCuBA alignment in the{' '}
        <Link
          to="/security?tab=best-practices"
          className="inline-flex items-center gap-1 not-italic text-[color:var(--color-secondaryBlue-300,#4F86A8)] underline-offset-2 hover:underline"
        >
          Best Practice Analyzer
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </Link>
        .
      </p>
    </>
  )
}

/** Hero score tile — left column of the snapshot grid. */
function ScoreTile({ score, tone }: { score: number; tone: string }) {
  return (
    <div className="col-span-2 rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] p-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
        Overall score
      </div>
      <div
        className="font-display mt-2 text-[44px] font-extrabold leading-none tabular-nums"
        style={{ color: tone, letterSpacing: '-0.03em' }}
      >
        {score}%
      </div>
      <div className="mt-2 text-[11px] italic text-[color:var(--color-faint,#9CA3AF)]">
        Composite across all assessed controls.
      </div>
    </div>
  )
}

/** Compact KPI tile for one result bucket. Accent color drives the numeral. */
function ResultTile({
  label,
  value,
  icon,
  accent,
}: {
  label: string
  value: number
  icon: React.ReactNode
  accent: string
}) {
  return (
    <div className="rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] p-5">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
        <span style={{ color: accent }}>{icon}</span>
        <span>{label}</span>
      </div>
      <div
        className="font-display mt-2 text-[28px] font-extrabold leading-none tabular-nums"
        style={{ color: accent, letterSpacing: '-0.02em' }}
      >
        {value.toLocaleString()}
      </div>
    </div>
  )
}

/** Editorial pill used on the group-row right-hand side. */
function ResultPill({
  result,
  count,
  suffix,
}: {
  result: ScubaControlResult
  count?: number
  suffix: string
}) {
  const style = RESULT_CHIP[result]
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
      style={{
        color: style.color,
        background: style.background,
        borderColor: style.border,
      }}
    >
      {count != null ? `${count} ${suffix}` : suffix}
    </span>
  )
}

/** A single control card inside an expanded group. Severity-tinted left border. */
function ControlCard({ control }: { control: ScubaControl }) {
  const chip = RESULT_CHIP[control.result] ?? RESULT_CHIP.NotApplicable
  return (
    <div
      className="rounded-[4px] border bg-[color:var(--color-paper,#fff)] p-4"
      style={{ borderColor: controlBorderAccent(control.result), borderLeftWidth: 3 }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-mute,#6B7280)]">
            Requirement
          </div>
          <p className="mt-1 text-[13px] font-medium text-[color:var(--color-navy,#212938)]">
            {control.requirement}
          </p>

          {control.details && (
            <div className="mt-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-mute,#6B7280)]">
                Details
              </div>
              <p
                className="mt-1 text-[12px] leading-relaxed text-[color:var(--color-navy,#212938)]/85 [&_a]:text-[color:var(--color-secondaryBlue-300,#4F86A8)] [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:opacity-80"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(control.details) }}
              />
            </div>
          )}
        </div>

        <span
          className="inline-flex flex-shrink-0 items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
          style={{
            color: chip.color,
            background: chip.background,
            borderColor: chip.border,
          }}
        >
          {chip.label}
        </span>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* SCuBA-specific brief composer + right-rail aside                           */
/* -------------------------------------------------------------------------- */

/**
 * Compose the standfirst copy from the latest SCuBA scan summary. SCuBA isn't
 * a Guardian-Score pillar, so we don't reuse `composeSecurityBrief` — the
 * editorial framing here is "point-in-time audit" rather than "continuous
 * posture". Verdict + delta are required by the PostureBrief shape but unused
 * because `<PillarBrief>` is rendered with a custom `aside` that replaces the
 * ScoreBox on this tab.
 */
function composeScubaBrief(summary: ScubaSummary): PostureBrief {
  const failures = summary.totalFailures ?? 0
  const errors = summary.totalErrors ?? 0
  const warnings = summary.totalWarnings ?? 0
  const products = summary.products ?? []
  const productCount = products.length

  // Find the weakest workload by compliance score so the headline can name a
  // subject ("Entra ID needs attention.") instead of just citing a count.
  const weakest = [...products].sort(
    (a, b) => (a.complianceScore ?? 100) - (b.complianceScore ?? 100),
  )[0]
  const weakestLabel = weakest
    ? getProductInfo(weakest.productName).displayName
    : null

  // Three editorial states: needs-attention (failures or errors present),
  // warnings-only (clean fails but warnings open), and spotless.
  const hasBlockers = failures > 0 || errors > 0
  const hasWarnings = warnings > 0

  let headlineSubject: string
  let headlineModifier: string
  let weakLink: string
  let verdict: string

  // PillarBrief renders the headline as `{subject} <em>{modifier}</em>.` —
  // it appends the period itself, so modifier strings must not include one.
  if (hasBlockers && weakestLabel) {
    const totalBlockers = failures + errors
    headlineSubject = weakestLabel
    headlineModifier = 'needs attention'
    weakLink = `${totalBlockers} ${totalBlockers === 1 ? 'control' : 'controls'} failing across ${productCount} ${productCount === 1 ? 'workload' : 'workloads'}.`
    verdict = 'Findings open. Worth a review.'
  } else if (hasWarnings) {
    headlineSubject = 'All baselines'
    headlineModifier = 'look clean'
    weakLink = `${warnings} ${warnings === 1 ? 'warning' : 'warnings'} open for review.`
    verdict = 'Mostly clean. Warnings worth reviewing.'
  } else {
    headlineSubject = 'Spotless'
    headlineModifier = 'across every workload'
    weakLink = 'Re-run when configurations change.'
    verdict = 'Spotless across every workload.'
  }

  const scanDate = summary.lastScanDate ? new Date(summary.lastScanDate) : null
  const scanDateLabel = scanDate
    ? scanDate.toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
      })
    : null

  const deck = scanDateLabel
    ? `Last scanned **${scanDateLabel}** against ${summary.totalControls ?? 0} CISA SCuBA controls. Some baselines are stricter than your tenant may need — **contact iuvo** for guidance on which to act on.`
    : `Scan run against ${summary.totalControls ?? 0} CISA SCuBA controls. Some baselines are stricter than your tenant may need — **contact iuvo** for guidance on which to act on.`

  return {
    kicker: 'SCuBA · Deep scan',
    headlineSubject,
    headlineModifier,
    weakLink,
    deck,
    verdict,
    byline: scanDate
      ? `Scan by Guardian SCuBA Runner · ${scanDate.toLocaleString()}`
      : 'Scan by Guardian SCuBA Runner',
    delta: { value: 0, days: 0 },
  }
}

/**
 * Right-rail aside that replaces the default ScoreBox on the SCuBA tab.
 *
 * SCuBA findings don't roll into Guardian Score, so a 0-100 ScoreBox would
 * mis-frame the page as a continuous metric. Instead we surface the scan
 * snapshot (compliance % framed as "of N CISA controls"), the run-state
 * controls, and last-scanned timestamp — the questions someone actually
 * opens this page to ask.
 */
function ScubaScanAside({
  score,
  tone,
  summary,
  isScanning,
  currentStep,
  onScan,
}: {
  score: number
  tone: string
  summary: ScubaSummary
  isScanning: boolean
  currentStep?: string | null
  onScan: () => void
}) {
  const totalControls = summary.totalControls ?? 0
  const passes = summary.totalPasses ?? 0
  const failures = summary.totalFailures ?? 0
  const warnings = summary.totalWarnings ?? 0

  return (
    <aside
      aria-label="SCuBA scan snapshot"
      className="flex flex-col rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[#212938] p-6 text-white"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
        Latest scan
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span
          className="font-display text-[72px] font-extrabold leading-none tabular-nums"
          style={{ color: tone, letterSpacing: '-0.03em' }}
        >
          {score}
        </span>
        <span className="text-[24px] font-semibold text-white/45">%</span>
      </div>
      <div className="mt-1.5 text-[12px] italic text-white/65">
        of {totalControls} CISA SCuBA controls
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <ScanResultPill label={`${passes} pass`} color={RESULT_CHIP.Pass.color} />
        <ScanResultPill label={`${failures} fail`} color={RESULT_CHIP.Fail.color} />
        {warnings > 0 && (
          <ScanResultPill
            label={`${warnings} warn`}
            color={RESULT_CHIP.Warning.color}
          />
        )}
      </div>

      {summary.lastScanDate && (
        <>
          <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
            Last scanned
          </div>
          <div className="mt-1 text-[13px] text-white/85">
            {new Date(summary.lastScanDate).toLocaleString()}
          </div>
        </>
      )}

      <button
        type="button"
        onClick={onScan}
        disabled={isScanning}
        className="mt-auto inline-flex items-center justify-center gap-2 self-start rounded-full bg-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#212938] transition-colors hover:bg-white/85 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <ArrowPathIcon
          className={`h-3.5 w-3.5 ${isScanning ? 'animate-spin' : ''}`}
        />
        {isScanning ? currentStep || 'Scanning…' : 'Re-run scan'}
      </button>
    </aside>
  )
}

function ScanResultPill({ label, color }: { label: string; color: string }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em]"
      style={{
        color,
        borderColor: `${color}66`,
        background: `${color}1A`,
      }}
    >
      {label}
    </span>
  )
}
